import { useMutation } from '@tanstack/react-query';
import { ArrowRight, Check, Mail } from 'lucide-react';
import { useState } from 'react';
import { Navigate } from 'react-router';

import { Button, Input, Logotype } from '@axioma/ui';

import { api, extractErrorMessage } from '../lib/api.js';
import { useAuthStore } from '../stores/auth.js';

export function Login(): JSX.Element {
  const token = useAuthStore((s) => s.token);
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: async (email: string) => {
      await api.post('/auth/magic-link', { email });
    },
    onError: (err) => setError(extractErrorMessage(err)),
  });

  if (token) {
    return <Navigate to="/" replace />;
  }

  const submitted = mutation.isSuccess;

  return (
    <div className="ax-hero-bg min-h-screen w-screen flex items-center justify-center px-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-12">
          <Logotype size="lg" />
          <p className="ax-editorial italic text-ax-text-secondary mt-3 text-base">
            Antes da prova, o axioma.
          </p>
        </div>

        {submitted ? (
          <div className="bg-ax-graphite border border-ax-line rounded-ax-lg p-8 text-center animate-ax-fade-in">
            <div className="inline-flex items-center justify-center h-12 w-12 rounded-full bg-[rgba(0,229,192,0.10)] border border-[rgba(0,229,192,0.30)] mb-4">
              <Check size={20} className="text-ax-electric" />
            </div>
            <h1 className="font-display text-2xl text-ax-text-primary tracking-tight mb-2">
              Verifique seu email
            </h1>
            <p className="ax-body text-ax-text-secondary">
              Enviamos um link de acesso para <strong className="text-ax-text-primary font-medium">{email}</strong>.
              Ele expira em 15 minutos e só pode ser usado uma vez.
            </p>
            <p className="font-mono text-[11px] text-ax-text-tertiary mt-6 uppercase tracking-wider">
              Não recebeu? Verifique a pasta de spam ou{' '}
              <button
                type="button"
                onClick={() => mutation.reset()}
                className="text-ax-aurora underline hover:text-ax-aurora-glow"
              >
                tente novamente
              </button>
            </p>
          </div>
        ) : (
          <div className="bg-ax-graphite border border-ax-line rounded-ax-lg p-8">
            <h1 className="font-display text-3xl text-ax-text-primary tracking-tight mb-1.5">
              Entrar
            </h1>
            <p className="ax-body-sm text-ax-text-tertiary mb-7">
              Acesso passwordless por link único enviado ao seu email institucional.
            </p>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                setError(null);
                mutation.mutate(email);
              }}
              className="space-y-5"
            >
              <Input
                label="Email"
                type="email"
                autoComplete="email"
                required
                placeholder="seu@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                leftAdornment={<Mail size={14} />}
                error={error ?? undefined}
              />
              <Button
                type="submit"
                variant="primary"
                size="lg"
                fullWidth
                loading={mutation.isPending}
                rightIcon={<ArrowRight size={16} />}
              >
                Receber link de acesso
              </Button>
            </form>

            <p className="font-mono text-[10px] text-ax-text-tertiary mt-7 uppercase tracking-wider leading-relaxed">
              2FA é obrigatório · Sigilo profissional como cláusula pétrea
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
